import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, ArrowRight, Mail, MessageCircle, Sparkles } from 'lucide-react';
import SEO from '@/components/SEO';
import FAQS from '@/data/faqs';

const FAQPage = () => {
  const [openIdx, setOpenIdx] = useState(0);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };

  return (
    <div
      className="min-h-screen pt-28 pb-24"
      style={{
        background:
          'linear-gradient(180deg, #F4F1EA 0%, #E8EEEF 45%, #DCE5E7 100%)',
      }}
      data-testid="faq-page"
    >
      <SEO
        title="Frequently Asked Questions"
        description="Answers to common questions about custom resin art, shipping, personalization, and ordering from Navs Art Studio in Vancouver."
        path="/faq"
        jsonLd={jsonLd}
      />

      {/* Decorative ocean shimmer */}
      <div
        aria-hidden
        className="absolute top-20 left-0 right-0 h-[1px] opacity-40"
        style={{
          background:
            'linear-gradient(90deg, transparent 0%, rgba(212,175,90,0.6) 50%, transparent 100%)',
        }}
      />

      <div className="max-w-3xl mx-auto px-6 md:px-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          className="text-center mb-16"
        >
          <p
            className="text-[11px] uppercase tracking-[0.5em] mb-5 flex items-center justify-center gap-2"
            style={{ color: '#0B1F26' }}
          >
            <Sparkles size={12} className="text-[#D4AF5A]" />
            <span>Helpful answers</span>
            <Sparkles size={12} className="text-[#D4AF5A]" />
          </p>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-6 tracking-[-0.01em]"
            style={{ fontFamily: 'Playfair Display, serif', color: '#0B1F26', fontWeight: 500 }}
            data-testid="faq-title"
          >
            Frequently Asked <em className="italic" style={{ color: '#1E4A55' }}>Questions</em>
          </h1>
          <p className="text-[#4A4A4A] text-base sm:text-lg leading-relaxed max-w-xl mx-auto">
            We&apos;ve answered common questions to make your experience easier.
          </p>
        </motion.div>

        {/* Accordion */}
        <div className="space-y-3" data-testid="faq-accordion">
          {FAQS.map((item, i) => {
            const isOpen = openIdx === i;
            return (
              <motion.div
                key={item.q}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, delay: 0.08 * i, ease: 'easeOut' }}
                className={`group rounded-2xl border bg-white/85 backdrop-blur-sm transition-all duration-500 ${
                  isOpen
                    ? 'border-[#D4AF5A]/55 shadow-[0_18px_48px_-22px_rgba(11,31,38,0.35)]'
                    : 'border-[#0B1F26]/8 shadow-[0_8px_28px_-22px_rgba(11,31,38,0.25)] hover:border-[#0B1F26]/20 hover:shadow-[0_14px_36px_-22px_rgba(11,31,38,0.3)]'
                }`}
                data-testid={`faq-item-${i}`}
              >
                <button
                  type="button"
                  onClick={() => setOpenIdx(isOpen ? -1 : i)}
                  aria-expanded={isOpen}
                  aria-controls={`faq-panel-${i}`}
                  className="w-full flex items-center justify-between gap-6 text-left px-6 sm:px-8 py-5 sm:py-6 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF5A]/60 rounded-2xl"
                  data-testid={`faq-question-${i}`}
                >
                  <span
                    className={`text-base sm:text-lg leading-snug transition-colors duration-300 ${
                      isOpen ? 'text-[#0B1F26]' : 'text-[#1A1A1A] group-hover:text-[#0B1F26]'
                    }`}
                    style={{ fontFamily: 'Playfair Display, serif', fontWeight: 500 }}
                  >
                    {item.q}
                  </span>
                  <span
                    className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center border transition-all duration-500 ${
                      isOpen
                        ? 'bg-[#0B1F26] border-[#0B1F26] rotate-[135deg]'
                        : 'bg-white border-[#0B1F26]/15 group-hover:border-[#D4AF5A]/70'
                    }`}
                  >
                    <Plus
                      size={16}
                      strokeWidth={1.6}
                      className={`transition-colors duration-300 ${
                        isOpen ? 'text-[#E8D9B5]' : 'text-[#0B1F26]'
                      }`}
                    />
                  </span>
                </button>

                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      key="panel"
                      id={`faq-panel-${i}`}
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                      className="overflow-hidden"
                      data-testid={`faq-answer-${i}`}
                    >
                      <div className="px-6 sm:px-8 pb-6 sm:pb-7 pt-1">
                        <div
                          className="h-[1px] w-12 mb-5"
                          style={{ background: 'linear-gradient(90deg, #D4AF5A 0%, transparent 100%)' }}
                        />
                        <p className="text-[#3F4F55] text-[15px] sm:text-base leading-[1.75]">
                          {item.a}
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.7, ease: 'easeOut' }}
          className="mt-20 rounded-3xl px-8 sm:px-12 py-12 sm:py-14 text-center relative overflow-hidden"
          style={{
            background:
              'linear-gradient(135deg, #0B1F26 0%, #143942 55%, #1E4A55 100%)',
          }}
          data-testid="faq-bottom-cta"
        >
          {/* Subtle gold sheen */}
          <div
            aria-hidden
            className="absolute -top-1/2 -right-1/4 w-[60%] h-[200%] opacity-[0.07] pointer-events-none"
            style={{
              background:
                'radial-gradient(ellipse at center, #D4AF5A 0%, transparent 60%)',
            }}
          />
          <p
            className="text-[10px] sm:text-[11px] uppercase tracking-[0.5em] mb-4"
            style={{ color: '#D4AF5A' }}
          >
            We&apos;re here to help
          </p>
          <h2
            className="text-white text-3xl sm:text-4xl lg:text-[44px] leading-tight mb-8"
            style={{ fontFamily: 'Playfair Display, serif', fontWeight: 500 }}
          >
            Still have <em className="italic" style={{ color: '#E8D9B5' }}>questions?</em>
          </h2>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/faq" data-testid="faq-cta-view-full">
              <button
                className="group rounded-full px-8 py-[14px] text-xs tracking-[0.18em] uppercase font-medium text-[#0B1F26] transition-all duration-500 hover:shadow-[0_10px_30px_rgba(212,175,90,0.45)] hover:-translate-y-0.5"
                style={{
                  background:
                    'linear-gradient(135deg, #F1E0B0 0%, #D4AF5A 55%, #B8923E 100%)',
                }}
              >
                <span className="flex items-center gap-2">
                  View Full FAQ
                  <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                </span>
              </button>
            </Link>
            <Link to="/contact" data-testid="faq-cta-contact">
              <button className="group rounded-full px-8 py-[14px] text-xs tracking-[0.18em] uppercase font-medium text-white border border-white/30 backdrop-blur-md bg-white/5 hover:bg-white/15 hover:border-white/70 transition-all duration-500 hover:-translate-y-0.5">
                <span className="flex items-center gap-2">
                  <Mail size={14} />
                  Contact Us
                </span>
              </button>
            </Link>
          </div>
          <p className="mt-8 text-white/55 text-xs tracking-wide flex items-center justify-center gap-2">
            <MessageCircle size={12} className="text-[#D4AF5A]" />
            Typical reply within 24 hours
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default FAQPage;
